import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import ChoiceRevealOnScroll from "../shared/ChoiceRevealOnScroll.jsx";
import { fadeUpVariants, staggerContainer } from "../shared/animations.js";

const OperationalFilterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const pillars = [
    {
      step: "01",
      title: "Isolate from the Noise",
      description:
        "Humans only enter the loop when a lead has passed a strict qualification protocol and is already booked on the calendar. We eliminate blind calls, out-of-service inquiries, and dead-end meetings.",
    },
    {
      step: "02",
      title: "Flawless CRM Hygiene",
      description:
        "The digital employee does not suffer from fatigue. It executes your exact qualification protocol with 100% accuracy. All transcripts, facts, and metrics are autonomously structured and logged into your database.",
    },
    {
      step: "03",
      title: "The Perfect Handoff",
      description:
        'Your human team receives a perfectly assembled client dossier before they ever say "Hello." Your people close—the digital employee has already qualified, logged, and booked. Their focus stays exclusively on vetted, high-priority revenue.',
    },
  ];

  return (
    <section className="section-padding mesh-gradient relative overflow-hidden" data-testid="operational-filter-section" ref={ref}>
      <div className="absolute inset-0 bg-gradient-to-br from-teal-500/[0.04] via-transparent to-transparent pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <ChoiceRevealOnScroll className="lg:sticky lg:top-28">
            <span className="badge-mono text-teal-400 bg-teal-500/10 border border-teal-500/20 px-3 py-1.5 rounded-full inline-block mb-6">
              THE OPERATIONAL FILTER
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight text-left" data-testid="operational-filter-headline">
              Zero Friction.
              <br />
              Absolute <span className="gradient-text">Protocol Enforcement.</span>
            </h2>
            <p className="text-lg text-gray-400 font-body leading-relaxed text-left" data-testid="operational-filter-intro">
              Your best people are burning out because{" "}
              <span className="text-teal-400 font-semibold">80%</span> of their time is spent on unqualified leads and administrative data entry. The digital employee replies in{" "}
              <span className="text-teal-400 font-semibold">9 seconds</span>, qualifies, logs, and books—acting as an absolute operational shield on the path to{" "}
              <span className="text-teal-400 font-semibold">2x more qualified appointments</span>.
            </p>

            <div className="mt-8 hidden lg:flex items-center gap-3 text-xs font-mono text-gray-500">
              <span className="w-2 h-2 rounded-full bg-teal-400 pulse-subtle" />
              <span>9-SEC REPLY → PROTOCOL → 2x QUALIFIED OUT</span>
            </div>
          </ChoiceRevealOnScroll>

          <motion.div
            className="relative"
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            <div className="absolute left-[19px] top-6 bottom-6 w-px bg-gradient-to-b from-teal-500/50 via-teal-500/20 to-teal-500/50 hidden sm:block" />

            <div className="space-y-6">
              {pillars.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  className="relative flex gap-5 sm:gap-6"
                  variants={fadeUpVariants}
                  data-testid={`operational-filter-pillar-${index}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-teal-500/10 border border-teal-500/30 flex items-center justify-center">
                    <span className="text-teal-400 font-mono text-xs font-bold">{pillar.step}</span>
                  </div>

                  <div className="flex-1 bento-card p-6 sm:p-7 border border-white/[0.06] bg-white/[0.02] hover:border-teal-500/20 transition-colors duration-300">
                    <h3 className="text-lg sm:text-xl font-bold mb-2 text-white">{pillar.title}</h3>
                    <p className="text-gray-400 font-body leading-relaxed text-sm sm:text-base">{pillar.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OperationalFilterSection;
